<script setup>
import { ref, computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'

/**
 * Split button: the main part puts the edited flow on air with the current
 * transition; the ▾ part opens a small popover to pick Cut/Fade + duration.
 * Before a session exists it reads "Start with this flow" (same endpoint,
 * only the active flag changes until Go live).
 */
const props = defineProps({
	/** useMediaflowDesk() instance (owns transition state + sendToLive). */
	desk: { type: Object, required: true },
	sessionActive: { type: Boolean, default: false },
	/** Why the button is disabled; empty when it can be used. */
	disabledReason: { type: String, default: '' },
	size: { type: String, default: 'md' },
	/** Stretch to the container (mobile bottom bar). */
	fullWidth: { type: Boolean, default: false },
})

const emit = defineEmits(['send'])

const menu = ref(null)
const TRANSITIONS = ['Cut', 'Fade']
const label = computed(() => (props.sessionActive ? 'Send to live' : 'Start with this scene'))
const disabled = computed(() => !!props.disabledReason)
const sending = computed(() => !!props.desk.sending.value)

const summary = computed(() => (props.desk.transitionType.value === 'Cut'
	? 'Cut'
	: `Fade · ${parseInt(props.desk.transitionDuration.value, 10) || 300} ms`))

// DropDownMenu hands `customClasses` straight to its toggle button, so the
// ▾ half is styled here to match a `main` FormButton.
const optionsClass = computed(() => [
	'inline-flex items-center justify-center rounded-r-md border-l border-l-white/20 text-white transition-colors',
	props.size === 'lg' ? 'h-10 w-9' : props.size === 'sm' ? 'h-7 w-7' : 'h-8 w-8',
	disabled.value ? 'bg-surface-5 text-surface-7 cursor-not-allowed pointer-events-none' : 'bg-main hover:bg-main-light',
].join(' '))

const setType = (type) => { props.desk.transitionType.value = type }
const onDuration = (value) => {
	const n = parseInt(value, 10)
	props.desk.transitionDuration.value = Number.isNaN(n) ? 300 : Math.max(50, Math.min(10000, n))
}
</script>

<template>
	<div class="inline-flex items-stretch" :class="fullWidth ? 'w-full' : ''" data-testid="send-to-live">
		<Tooltip :active="disabled" :content="disabledReason" wrapper-class="inline-flex min-w-0" :class="fullWidth ? 'flex-1' : ''">
			<FormButton
				type="main"
				:size="size"
				:disabled="disabled || sending"
				:is-loading="sending"
				:full-width="fullWidth"
				class="font-semibold"
				:class="sessionActive ? '!rounded-r-none' : ''"
				:title="disabled ? disabledReason : `${label} (${summary})`"
				data-testid="send-to-live-main"
				@click="emit('send')"
			>
				{{ label }}
			</FormButton>
		</Tooltip>
		<DropDownMenu
			v-if="sessionActive"
			ref="menu"
			type="main"
			:size="size"
			position="right"
			prevent-hide
			:disabled="disabled"
			aria-label="Transition options"
			:custom-classes="optionsClass"
			max-height="none"
			list-classes="min-w-[220px] !overflow-visible !py-0"
			data-testid="send-to-live-options"
		>
			<template #toggle-button>
				<icon-base width="12" height="12" fill="none"><icon-chevron-down /></icon-base>
			</template>
			<template #menu-items>
				<li class="flex flex-col gap-3 p-3 pb-6" @click.stop>
					<span class="text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">Transition</span>
					<TabGroup type="segmented" size="sm" :cols="2">
						<TabItem
							v-for="t in TRANSITIONS"
							:key="t"
							:is-active="desk.transitionType.value === t"
							:aria-pressed="desk.transitionType.value === t"
							@click="setType(t)"
						>
							{{ t }}
						</TabItem>
					</TabGroup>
					<FormInput
						type="number"
						size="sm"
						label="Duration (ms)"
						:model-value="desk.transitionDuration.value"
						:disabled="desk.transitionType.value === 'Cut'"
						:helper="desk.transitionType.value === 'Cut' ? 'Cut switches instantly.' : '50 – 10000 ms'"
						@update:model-value="onDuration"
					/>
				</li>
			</template>
		</DropDownMenu>
	</div>
</template>
